// AutocompleteMarca — input de marca com sugestoes da lista cadastrada.
// Permite digitar texto livre ("Usar X") e abrir modal pra cadastrar nova marca.
// Portado do ControleFC/frontend/src/pages/Cotacoes/CotacaoRespostas.jsx (linhas 34-143).
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { upperMantendoCursor } from './inputs/UpperInput';
import { Plus } from 'lucide-react';

export interface MarcaOpcao {
  id: number;
  nome: string;
}

interface Props {
  value: string | null | undefined;
  onChange: (valor: string) => void;
  marcas: MarcaOpcao[];
  onNovaMarca: () => void;
  placeholder?: string;
  inputStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
}

export function AutocompleteMarca({
  value,
  onChange,
  marcas,
  onNovaMarca,
  placeholder = 'Marca',
  inputStyle,
  wrapperStyle,
}: Props) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState(value || '');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setBusca(value || '');
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const buscaUpper = busca.toUpperCase();
  const filtradas = marcas.filter((m) => m.nome.toUpperCase().includes(buscaUpper));
  const showUsar = !!busca && !marcas.some((m) => m.nome === busca);

  const options = useMemo(() => {
    const opts: { kind: 'usar' | 'nova' | 'marca'; label: string; action: () => void }[] = [];
    if (showUsar) {
      opts.push({
        kind: 'usar',
        label: `Usar "${busca}"`,
        action: () => {
          onChange(busca);
          setAberto(false);
        },
      });
    }
    opts.push({
      kind: 'nova',
      label: 'Nova Marca',
      action: () => {
        setAberto(false);
        onNovaMarca();
      },
    });
    filtradas.forEach((m) => {
      opts.push({
        kind: 'marca',
        label: m.nome,
        action: () => {
          onChange(m.nome);
          setBusca(m.nome);
          setAberto(false);
        },
      });
    });
    return opts;
  }, [showUsar, busca, filtradas, onChange, onNovaMarca]);

  useEffect(() => {
    setHighlightIdx(0);
  }, [busca, aberto]);

  useEffect(() => {
    if (!aberto || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${highlightIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, aberto]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!aberto && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setAberto(true);
      return;
    }
    if (!aberto) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlightIdx];
      if (opt) opt.action();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setAberto(false);
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const next = e.relatedTarget as Node | null;
    if (!next || !ref.current?.contains(next)) {
      window.setTimeout(() => {
        setAberto(false);
        if (busca && busca !== value) onChange(busca);
      }, 150);
    }
  }

  const baseInputStyle: CSSProperties = {
    width: '100%',
    padding: '2px 4px',
    border: '1px solid #d1d5db',
    borderRadius: 3,
    fontSize: 11,
    background: '#fff',
  };

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: 0, ...wrapperStyle }}>
      <input
        type="text"
        value={busca}
        onChange={(e) => {
          setBusca(upperMantendoCursor(e));
          setAberto(true);
        }}
        onFocus={() => setAberto(true)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        style={{ ...baseInputStyle, ...inputStyle }}
      />
      {aberto && (
        <div
          ref={listRef}
          style={{
            position: 'absolute',
            zIndex: 50,
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 2,
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxHeight: 192,
            overflowY: 'auto',
          }}
        >
          {options.map((opt, idx) => {
            const isHighlight = idx === highlightIdx;
            const cor =
              opt.kind === 'usar'
                ? 'var(--primary)'
                : opt.kind === 'nova'
                  ? '#16a34a'
                  : opt.label === value
                    ? '#111827'
                    : '#374151';
            const fundo = isHighlight ? (opt.kind === 'nova' ? '#dcfce7' : 'var(--primary-soft)') : 'transparent';
            const peso =
              opt.kind === 'usar' || opt.kind === 'nova' || opt.label === value ? 600 : 400;
            const borderBottom =
              opt.kind === 'usar' || opt.kind === 'nova' ? '1px solid #f3f4f6' : 'none';
            return (
              <button
                key={`${opt.kind}-${idx}`}
                type="button"
                data-idx={idx}
                onMouseEnter={() => setHighlightIdx(idx)}
                onClick={opt.action}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  width: '100%',
                  padding: '4px 8px',
                  fontSize: 11,
                  textAlign: 'left',
                  background: fundo,
                  color: cor,
                  fontWeight: peso,
                  border: 'none',
                  borderBottom,
                  cursor: 'pointer',
                }}
              >
                {opt.kind === 'nova' && <Plus size={12} />}
                {opt.label}
              </button>
            );
          })}
          {filtradas.length === 0 && !showUsar && (
            <div style={{ padding: '4px 8px', fontSize: 11, color: '#9ca3af' }}>
              Nenhuma marca encontrada
            </div>
          )}
        </div>
      )}
    </div>
  );
}
